import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useData } from "@/context/DataContext";
import { getRoleLine } from "@/lib/roleLabel";
import {
    CommandDialog,
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
    CommandShortcut,
    CommandSeparator,
} from "@/components/ui/command";
import {
    LayoutDashboard,
    CalendarDays,
    Clock,
    BookOpen,
    Users,
    Building2,
    UserCog,
    SlidersHorizontal,
    User,
    CalendarClock,
    ArrowRight,
    Search as SearchIcon,
    GraduationCap,
    Compass,
    Network,
} from "lucide-react";

const MONTH_NAMES = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const fmtMonth = (ym) => { if (!ym) return "—"; const [y,m] = ym.split("-"); return `${MONTH_NAMES[parseInt(m,10)-1]} ${y}`; };

const CommandPalette = () => {
    const [open, setOpen] = useState(false);
    const [sessions, setSessions] = useState([]);
    const [roaSessions, setRoaSessions] = useState([]);
    const [attendance, setAttendance] = useState([]);
    const navigate = useNavigate();
    const { user } = useAuth();
    const { users, depts } = useData();

    // Global Cmd+K / Ctrl+K
    useEffect(() => {
        const down = (e) => {
            if ((e.key === "k" || e.key === "K") && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                setOpen((v) => !v);
            }
        };
        document.addEventListener("keydown", down);
        return () => document.removeEventListener("keydown", down);
    }, []);

    // Lazy-load sessions/attendance the first time palette opens
    useEffect(() => {
        if (!open) return;
        if (sessions.length === 0) {
            api.get("/sessions").then((r) => setSessions(r.data || [])).catch(() => {});
        }
        if (roaSessions.length === 0) {
            api.get("/roa/sessions").then((r) => setRoaSessions(r.data || [])).catch(() => {});
        }
        if (attendance.length === 0) {
            api.get("/attendance").then((r) => setAttendance(r.data || [])).catch(() => {});
        }
    }, [open]);

    const go = (path) => {
        setOpen(false);
        navigate(path);
    };

    const deptById = useMemo(() => Object.fromEntries((depts || []).map((d) => [d.id, d])), [depts]);

    // Build searchable items
    const activeUsers = useMemo(
        () => (users || []).filter((u) => u.active),
        [users]
    );

    const navItems = useMemo(() => {
        const base = [
            { id: "nav-dashboard", label: "Dashboard", hint: "Overview & insights", icon: LayoutDashboard, path: "/dashboard" },
            { id: "nav-calendar", label: "Calendar", hint: "PAS + ROA · year-view", icon: CalendarDays, path: "/calendar" },
            { id: "nav-calendar-pas", label: "Calendar · PAS only", hint: "Filtered to PAS sessions", icon: CalendarClock, path: "/calendar?type=pas" },
            { id: "nav-calendar-roa", label: "Calendar · ROA only", hint: "Roadmap of Association · 6-month", icon: Compass, path: "/calendar?type=roa" },
            { id: "nav-attendance", label: "Attendance", hint: "Monthly rollup", icon: Clock, path: "/attendance" },
            { id: "nav-kno", label: "KNO Attendance", hint: "Weekly knowledge session", icon: GraduationCap, path: "/kno" },
            { id: "nav-kno-checkin", label: "KNO Self Check-In", hint: "Beta · Wednesdays", icon: GraduationCap, path: "/kno/check-in" },
            { id: "nav-profile", label: "Profile", hint: "Your timeline, designation & joining date", icon: Users, path: "/profile" },
            { id: "nav-learning", label: "Learning Hub", hint: "Assigned resources", icon: BookOpen, path: "/learning" },
            { id: "nav-org", label: "Org Chart", hint: "Your team's reporting tree", icon: Network, path: "/org" },
        ];
        if (user?.role !== "employee") {
            base.push(
                { id: "nav-employees", label: "Employees", icon: Users, path: "/employees" },
                { id: "nav-departments", label: "Departments", icon: Building2, path: "/departments" },
                { id: "nav-managers", label: "Managers", icon: UserCog, path: "/managers" },
                { id: "nav-control-panel", label: "Control Panel", hint: "Admin settings", icon: SlidersHorizontal, path: "/control-panel" },
            );
        }
        return base;
    }, [user]);

    const quickActions = useMemo(() => {
        const items = [];
        if (user?.role !== "employee") {
            items.push({
                id: "qa-upload-attendance",
                label: "Upload attendance rollup",
                hint: "Jump to Control Panel → Attendance",
                icon: Clock,
                path: "/control-panel?tab=attendance",
            });
        }
        items.push(
            { id: "qa-compare-months", label: "Compare two months", hint: "Attendance side-by-side", icon: CalendarClock, path: "/attendance" },
            { id: "qa-my-profile", label: user?.role === "employee" ? "My timeline" : "View my PAS timeline", icon: User, path: `/employees/${user?.user_id}` },
        );
        return items;
    }, [user]);

    return (
        <>
            <CommandDialog open={open} onOpenChange={setOpen}>
                <CommandInput placeholder="Search employees, PAS cycles, attendance, pages…" data-testid="command-palette-input" />
                <CommandList>
                    <CommandEmpty>
                        <div className="py-8 text-slate-500 text-sm">
                            Nothing matched. Try a first name, a cycle number, or a month like "Nov 25".
                        </div>
                    </CommandEmpty>

                    <CommandGroup heading="Quick actions">
                        {quickActions.map((q) => (
                            <CommandItem
                                key={q.id}
                                value={`${q.label} ${q.hint || ""}`}
                                onSelect={() => go(q.path)}
                                data-testid={`cmd-${q.id}`}
                            >
                                <q.icon className="text-[#2EB4AC]" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-slate-900">{q.label}</div>
                                    {q.hint && <div className="text-xs text-slate-500">{q.hint}</div>}
                                </div>
                                <CommandShortcut>↵</CommandShortcut>
                            </CommandItem>
                        ))}
                    </CommandGroup>

                    <CommandSeparator />

                    <CommandGroup heading="Pages">
                        {navItems.map((n) => (
                            <CommandItem
                                key={n.id}
                                value={`${n.label} ${n.hint || ""}`}
                                onSelect={() => go(n.path)}
                                data-testid={`cmd-${n.id}`}
                            >
                                <n.icon className="text-slate-500" />
                                <div className="flex-1 min-w-0">
                                    <div className="text-slate-900">{n.label}</div>
                                    {n.hint && <div className="text-xs text-slate-500">{n.hint}</div>}
                                </div>
                                <ArrowRight className="h-3 w-3 text-slate-400" />
                            </CommandItem>
                        ))}
                    </CommandGroup>

                    {activeUsers.length > 0 && (
                        <>
                            <CommandSeparator />
                            <CommandGroup heading={`Employees · ${activeUsers.length}`}>
                                {activeUsers.map((u) => {
                                    const deptName = deptById[u.department_id]?.name || "";
                                    return (
                                        <CommandItem
                                            key={u.user_id}
                                            value={`${u.name} ${u.email} ${deptName} ${u.role}`}
                                            onSelect={() => go(`/employees/${u.user_id}`)}
                                            data-testid={`cmd-emp-${u.user_id}`}
                                        >
                                            <div className="h-7 w-7 shrink-0 rounded-full bg-teal-50 text-[#25908A] border border-teal-100 flex items-center justify-center text-[11px] font-bold uppercase">
                                                {(u.name || "?").charAt(0)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-slate-900 truncate">{u.name}</div>
                                                <div className="text-xs text-slate-500 truncate">
                                                    {getRoleLine(u, users)}
                                                    {deptName ? ` · ${deptName}` : ""}
                                                </div>
                                            </div>
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </>
                    )}

                    {sessions.length > 0 && (
                        <>
                            <CommandSeparator />
                            <CommandGroup heading="PAS sessions">
                                {sessions.slice(0, 40).map((s) => {
                                    const who = (users || []).find((x) => x.user_id === s.employee_id)?.name || "—";
                                    return (
                                        <CommandItem
                                            key={s.id}
                                            value={`${who} pas cycle ${s.cycle_number} ${s.scheduled_date} ${s.status}`}
                                            onSelect={() => go(`/employees/${s.employee_id}`)}
                                            data-testid={`cmd-sess-${s.id}`}
                                        >
                                            <CalendarClock className="text-slate-500" />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-slate-900 truncate">{who} · Cycle #{s.cycle_number}</div>
                                                <div className="text-xs text-slate-500">
                                                    Scheduled {s.scheduled_date} · {s.status}
                                                </div>
                                            </div>
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </>
                    )}

                    {roaSessions.length > 0 && (
                        <>
                            <CommandSeparator />
                            <CommandGroup heading="ROA sessions">
                                {roaSessions.slice(0, 40).map((s) => {
                                    const who = (users || []).find((x) => x.user_id === s.employee_id)?.name || "—";
                                    return (
                                        <CommandItem
                                            key={s.id}
                                            value={`${who} roa cycle ${s.cycle_number} ${s.scheduled_date} ${s.status} roadmap`}
                                            onSelect={() => go("/calendar?type=roa")}
                                            data-testid={`cmd-roa-${s.id}`}
                                        >
                                            <Compass className="text-indigo-500" />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-slate-900 truncate">{who} · ROA Cycle #{s.cycle_number}</div>
                                                <div className="text-xs text-slate-500">
                                                    Scheduled {s.scheduled_date} · {s.status}
                                                </div>
                                            </div>
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </>
                    )}

                    {attendance.length > 0 && (
                        <>
                            <CommandSeparator />
                            <CommandGroup heading="Attendance records">
                                {attendance.slice(0, 40).map((r) => (
                                    <CommandItem
                                        key={r.id || `${r.user_id}-${r.month}`}
                                        value={`${r.employee_name} ${r.department_name} ${fmtMonth(r.month)} attendance`}
                                        onSelect={() => go("/attendance")}
                                        data-testid={`cmd-att-${r.id}`}
                                    >
                                        <Clock className="text-slate-500" />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-slate-900 truncate">{r.employee_name} · {fmtMonth(r.month)}</div>
                                            <div className="text-xs text-slate-500">
                                                Total {r.total_instances || 0} · OFF {r.oof || 0} · WFH {r.wfh || 0}
                                            </div>
                                        </div>
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </>
                    )}
                </CommandList>
            </CommandDialog>
        </>
    );
};

export const CommandPaletteTrigger = ({ className = "" }) => {
    const trigger = () => {
        const evt = new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true });
        document.dispatchEvent(evt);
    };
    const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPod|iPad/.test(navigator.platform);
    return (
        <button
            onClick={trigger}
            data-testid="command-palette-trigger"
            className={`hidden md:inline-flex items-center gap-2 h-9 pl-2.5 pr-1.5 rounded-lg border border-slate-200 bg-slate-50/60 hover:border-[#2EB4AC] hover:bg-white text-slate-500 text-sm transition-colors ${className}`}
            aria-label="Open command palette"
        >
            <SearchIcon className="h-4 w-4" />
            <span className="text-slate-500">Search…</span>
            <kbd className="ml-2 inline-flex items-center gap-0.5 px-1.5 h-5 rounded bg-white border border-slate-200 text-[10px] font-mono text-slate-500">
                {isMac ? "⌘" : "Ctrl"} K
            </kbd>
        </button>
    );
};

export default CommandPalette;
